const CACHE_EVICTION_TIME = 1000 * 60 * 5;
const preloadedHtml = new Map();

function deepQuerySelector(root, selector) {
  let element = root.querySelector(selector);
  if (element) {
    return element;
  }

  for (const template of root.querySelectorAll('template')) {
    element = deepQuerySelector(template.content, selector);
    if (element) {
      return element;
    }
  }

  return null;
}

class VariantPickerV2 extends HTMLElement {
  #delegateAbortController;
  #intersectionObserver;
  #form;
  #selectedVariant = {};

  connectedCallback() {
    this.#selectedVariant = JSON.parse(this.querySelector('script[data-variant]')?.textContent || '{}');
    this.#form = document.forms[this.getAttribute('form-id')];
    this.#delegateAbortController = new AbortController();
    const { signal } = this.#delegateAbortController;

    const formId = this.getAttribute('form-id');
    const optionInputSelector = `input[data-option-position][form="${formId}"]`;

    document.body.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        if (!target.matches(optionInputSelector)) return;
        if (!this.contains(target)) return;
        this.#onOptionChanged(event);
      },
      { signal, capture: true }
    );

    document.body.addEventListener(
      'pointerenter',
      (event) => {
        const label = event.target?.closest?.(`${optionInputSelector}:not(:checked) + label`);
        if (!label || !this.contains(label)) return;
        const input = label.previousElementSibling;
        if (!(input instanceof HTMLInputElement)) return;
        this.#preloadOption(input);
      },
      { signal, capture: true }
    );

    document.body.addEventListener(
      'touchstart',
      (event) => {
        const label = event.target?.closest?.(`${optionInputSelector}:not(:checked) + label`);
        if (!label || !this.contains(label)) return;
        const input = label.previousElementSibling;
        if (!(input instanceof HTMLInputElement)) return;
        this.#preloadOption(input);
      },
      { signal, capture: true }
    );

    this.#intersectionObserver = new IntersectionObserver((entries) => {
      if (!entries[0]?.isIntersecting || !this.#form) return;

      const prerenderOptions = () => {
        Array.from(this.#form.elements)
          .filter((item) => item.matches?.('input[data-option-position]:not(:checked)'))
          .forEach((input) => this.#preloadOption(input));
      };

      if (window.requestIdleCallback) {
        window.requestIdleCallback(prerenderOptions, { timeout: 2000 });
      } else {
        prerenderOptions();
      }
    });

    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.#delegateAbortController?.abort();
    this.#intersectionObserver?.disconnect();
  }

  get selectedVariant() {
    return this.#selectedVariant;
  }

  get productHandle() {
    return this.getAttribute('handle');
  }

  get updateUrl() {
    return this.hasAttribute('update-url');
  }

  #getActiveOptionValues() {
    return Array.from(this.#form.elements)
      .filter((item) => item.matches('input[data-option-position]:checked'))
      .sort(
        (a, b) =>
          parseInt(a.getAttribute('data-option-position'), 10) -
          parseInt(b.getAttribute('data-option-position'), 10)
      )
      .map((input) => input.value);
  }

  #getOptionValuesFromOption(input) {
    return [
      input,
      ...Array.from(this.#form.elements).filter((item) =>
        item.matches(`input[data-option-position]:not([name="${input.name}"]):checked`)
      ),
    ]
      .sort(
        (a, b) =>
          parseInt(a.getAttribute('data-option-position'), 10) -
          parseInt(b.getAttribute('data-option-position'), 10)
      )
      .map((optionInput) => optionInput.value);
  }

  async #onOptionChanged(event) {
    if (!event.target.name.includes('option')) {
      return;
    }

    await this.selectCombination({
      optionValues: this.#getActiveOptionValues(),
      productChange: event.target.hasAttribute('data-product-url'),
    });
  }

  #preloadOption(input) {
    this.#renderForCombination(this.#getOptionValuesFromOption(input));
  }

  async selectCombination({ optionValues, productChange }) {
    const previousVariant = this.selectedVariant;
    const newContent = document.createRange().createContextualFragment(
      await this.#renderForCombination(optionValues)
    );

    if (!productChange) {
      const newVariantPicker = deepQuerySelector(
        newContent,
        `variant-picker-v2[form-id="${this.getAttribute('form-id')}"]`
      );
      const newVariant = JSON.parse(
        newVariantPicker?.querySelector('script[data-variant]')?.textContent || '{}'
      );

      this.#selectedVariant = newVariant;

      if (this.#form?.id) {
        this.#form.id.value = this.#selectedVariant?.id;
        this.#form.id.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (this.updateUrl && this.#selectedVariant?.id) {
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set('variant', this.#selectedVariant.id);
        window.history.replaceState({ path: newUrl.toString() }, '', newUrl.toString());
      }
    }

    this.#form?.dispatchEvent(
      new CustomEvent('product:rerender', {
        detail: {
          htmlFragment: newContent,
          productChange,
        },
      })
    );

    if (!productChange) {
      this.#form?.dispatchEvent(
        new CustomEvent('variant:change', {
          bubbles: true,
          detail: {
            formId: this.#form?.id,
            variant: this.#selectedVariant,
            previousVariant,
          },
        })
      );
    }

    window.Shopify?.PaymentButton?.init();
  }

  async #renderForCombination(optionValues) {
    const optionValuesAsString = optionValues.join(',');
    const hashKey = `${optionValuesAsString}-${this.getAttribute('section-id')}`;

    let productUrl = `${Shopify.routes.root}products/${this.productHandle}`;

    for (const optionValue of optionValues) {
      const inputForOptionValue = Array.from(this.#form.elements).find((item) =>
        item.matches(`input[value="${optionValue}"]`)
      );

      if (inputForOptionValue?.dataset.productUrl) {
        productUrl = inputForOptionValue.dataset.productUrl;
        break;
      }
    }

    if (!preloadedHtml.has(hashKey)) {
      const sectionQueryParam =
        this.getAttribute('context') === 'quick_buy'
          ? ''
          : `&section_id=${this.getAttribute('section-id')}`;
      const promise = fetch(
        `${productUrl}?option_values=${optionValuesAsString}${sectionQueryParam}`
      ).then((response) => response.text());

      preloadedHtml.set(hashKey, { htmlPromise: promise, timestamp: Date.now() });

      if (preloadedHtml.size > 100) {
        preloadedHtml.delete(Array.from(preloadedHtml.keys())[0]);
      }
    }

    const cached = preloadedHtml.get(hashKey);

    if (Date.now() - cached.timestamp > CACHE_EVICTION_TIME) {
      preloadedHtml.delete(hashKey);
      return this.#renderForCombination(optionValues);
    }

    return cached.htmlPromise;
  }
}

if (!window.customElements.get('variant-picker-v2')) {
  window.customElements.define('variant-picker-v2', VariantPickerV2);
}
